import { memo, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  render: (row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  loading?: boolean;
  emptyMessage?: string;
}

export const DataTable = memo(function DataTable<T>({
  columns,
  data,
  keyExtractor,
  sortColumn,
  sortDirection,
  onSort,
  loading = false,
  emptyMessage = '尚無資料',
}: DataTableProps<T>) {
  const headerCells = useMemo(
    () =>
      columns.map((col) => {
        const isSorted = sortColumn === col.key;
        return (
          <th
            key={col.key}
            className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider ${
              col.sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''
            } ${col.width ?? ''}`}
            onClick={() => col.sortable && onSort?.(col.key)}
          >
            <div className="flex items-center gap-1">
              {col.header}
              {col.sortable && (
                <span className="inline-flex">
                  {isSorted && sortDirection === 'asc' ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : isSorted && sortDirection === 'desc' ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronsUpDown className="h-3.5 w-3.5 text-gray-300" />
                  )}
                </span>
              )}
            </div>
          </th>
        );
      }),
    [columns, sortColumn, sortDirection, onSort],
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-brand-bake overflow-hidden">
        <table className="min-w-full divide-y divide-brand-bake">
          <thead className="bg-brand-cream">
            <tr>{headerCells}</tr>
          </thead>
        </table>
        <div className="p-12 text-center text-gray-400">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-bake border-t-brand-gold" />
          <p className="mt-3 text-sm">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-brand-bake overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-brand-bake">
          <thead className="bg-brand-cream">
            <tr>{headerCells}</tr>
          </thead>
          <tbody className="divide-y divide-brand-bake/50">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-gray-400 text-sm"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={keyExtractor(row)}
                  className={`transition-colors hover:bg-brand-cream/50 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-brand-cream/20'
                  }`}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap"
                    >
                      {col.render(row, index)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data.length > 0 && (
        <div className="px-4 py-3 border-t border-brand-bake bg-brand-cream/30 text-xs text-gray-400">
          共 {data.length} 筆資料
        </div>
      )}
    </div>
  );
}) as <T>(props: DataTableProps<T>) => React.ReactElement;
